import { GAME, MACHINE_ERA, isCrewed, openAnomaly, rogueOrigin, sabotageKey } from './game.js';
import type { Anomaly, Chapter, Meeting, ShowState, StopReason, StopRun, StopStep } from './types.js';

/**
 * A stop, one thing at a time (Marek, 2026-10-06, after the first evening: "people should be able to
 * finish the games before someone will set off the alarm or call a meeting"), and every step of it on
 * the clicker (2026-10-09: "full control over the evening with the clicker, no automatic triggers. I
 * need to control when they start sabotaging, alarms, votings, games and so on").
 *
 * While the games are on, everyone plays: the stop's saboteurs play their sabotage, which is their
 * game of the stop, and nothing interrupts anyone. Once every game is done the ship is charged, and the
 * show waits. Then each NEXT is one step (the reducer's `stopStep`):
 *
 *   charged  — SHIP CHARGED, until the first NEXT;
 *   alarms   — one alarm per saboteur drawn, one NEXT each, the timeline standing in for any the draw
 *              could not find; each plays as before (the countdown, FIX, fixed or damage). A NEXT while
 *              one is still on settles it at once;
 *   machines — in 2213, the machines take the ship (`MACHINE_DAMAGE`);
 *   meeting  — the table talks it over: who is the saboteur? No ballots yet;
 *   vote     — everyone picks the guest they suspect, no skipping (`tallyMeeting`), open until NEXT counts it;
 *   verdict  — who was voted out, and whether they were a saboteur;
 *   reveal   — the stop's saboteurs, and the points (`STOP_POINTS`);
 *   done     — the era's scores; NEXT jumps.
 *
 * Clocks still run inside a step — an alarm's countdown, a called phone handed on to the next — and
 * the room wakes itself for them (`nextDueAt`). None of them takes the show to its next step.
 */
export const STOP = {
  /** Kept for screens that pace their own pictures: SHIP CHARGED's moment. */
  CHARGED_MS: 6_000,
  /** An alarm's result on every screen. */
  AFTER_ALARM_MS: 8_000,
  /** 2213: the machines taking the ship. */
  MACHINES_MS: 10_000,
  /** The verdict. */
  VERDICT_MS: 10_000,
  /** The reveal and its points. */
  REVEAL_MS: 12_000,
} as const;

/**
 * What the stop's end scores (Marek, 2026-10-06, "points"): every guest who voted for one of the stop's
 * saboteurs; a saboteur caught loses, a guest voted out wrongly is made up for; a saboteur who got
 * away, and every damage a saboteur's own alarm did.
 */
export const STOP_POINTS = {
  RIGHT_VOTE: 300,
  CAUGHT: -500,
  WRONGLY_OUT: 200,
  ESCAPED: 300,
  DAMAGE: 300,
} as const;

/** This era's run, or null before its games are done. */
export function stopOf(state: { chapter: number; game: { stop?: StopRun | null } }): StopRun | null {
  const stop = state.game.stop ?? null;
  return stop && stop.chapter === state.chapter ? stop : null;
}

/** The stop's steps have all been taken: its scores are up, and the jump is next. */
export function stopDone(state: { chapter: number; game: { stop?: StopRun | null } }): boolean {
  return stopOf(state)?.step === 'done';
}

/** The guests drawn as this era's saboteurs, aboard now. Private until the reveal. */
export function saboteursNow(state: Pick<ShowState, 'seats'>): number[] {
  return state.seats.filter((s) => isCrewed(s) && s.alignment === 'PARADOX AGENT').map((s) => s.seat);
}

/** Who was a saboteur at this stop: every draw for it, kept in `agentStops`. */
export function saboteursAt(state: Pick<ShowState, 'game' | 'seats'>, chapter: number): number[] {
  const here = new Set(state.seats.filter(isCrewed).map((s) => s.seat));
  return Object.entries(state.game.agentStops ?? {})
    .filter(([seat, stops]) => stops.includes(chapter) && here.has(Number(seat)))
    .map(([seat]) => Number(seat))
    .sort((a, b) => a - b);
}

/** How many alarms the stop's end has: one per saboteur drawn, and the timeline's for any missing. */
export function alarmsPlanned(state: Pick<ShowState, 'seats' | 'game'>): number {
  if (!state.game.drawn) return 0;
  return Math.max(1, saboteursNow(state).filter((seat) => !state.seats.find((s) => s.seat === seat)?.brig).length + state.game.rogue);
}

/**
 * Where the k-th alarm of the stop comes from: the saboteurs in the order they finished their sabotage
 * (one who never did comes after), then the timeline for any the draw came up short of.
 */
export function alarmOrigin(state: ShowState, k: number): { origin: number; source: Anomaly['source']; by: number | null } | null {
  const armed = state.game.armed ?? {};
  const when = (seat: number) => armed[sabotageKey(state.chapter, seat)] ?? Number.POSITIVE_INFINITY;
  const agents = saboteursNow(state)
    .filter((seat) => !state.seats.find((s) => s.seat === seat)?.brig)
    .sort((a, b) => when(a) - when(b) || a - b);
  const agent = agents[k];
  if (agent !== undefined) return { origin: agent, source: 'agent', by: agent };
  const origin = rogueOrigin(state, k);
  return origin === null ? null : { origin, source: 'timeline', by: null };
}

/** The step after this one, once its alarms are counted. */
export function stepAfter(state: Pick<ShowState, 'chapter' | 'game'>, stop: StopRun): StopStep {
  switch (stop.step) {
    case 'charged':
      return stop.alarms > 0 ? 'alarms' : afterAlarms(state);
    case 'alarms':
      return stop.fired < stop.alarms ? 'alarms' : afterAlarms(state);
    case 'machines':
      return 'meeting';
    case 'meeting':
      return 'vote';
    case 'vote':
      return 'verdict';
    case 'verdict':
      return 'reveal';
    default:
      return 'done';
  }
}

function afterAlarms(state: Pick<ShowState, 'chapter' | 'game'>): StopStep {
  return state.chapter === MACHINE_ERA && state.game.uprising === null && state.game.drawn ? 'machines' : 'meeting';
}

/** The stop is between steps and its last alarm, if any, is over: what the screens show while NEXT waits. */
export function stopWaiting(state: ShowState): boolean {
  const stop = stopOf(state);
  if (!stop || stop.step === 'done') return false;
  if (stop.step === 'alarms' && openAnomaly(state.game)) return false;
  return !(stop.step === 'vote' && state.game.meeting);
}

/**
 * The next moment after `now` the room's clock has something to do inside a step — an alarm running
 * out, a called phone to replace, an old meeting closing, a table game ending — so a room can wake
 * itself then (the cloud room's alarm, the Mac's timer) rather than wait for a phone's heartbeat. Ask
 * it just after a change: everything due by `now` has been done. Null: nothing. The stop's steps
 * themselves are never due: they wait for NEXT.
 */
export function nextDueAt(state: ShowState, now: number): number | null {
  const times: number[] = [];
  const open = openAnomaly(state.game);
  if (open && !state.game.meeting) {
    times.push(open.deadline);
    for (const seat of open.called) {
      if (open.seen[String(seat)] === undefined) times.push((open.calledAt[String(seat)] ?? open.at) + GAME.FIX_ANSWER_MS);
    }
  }
  if (state.game.meeting && state.game.meeting.closesAt !== null) times.push(state.game.meeting.closesAt);
  if (state.tableGame) times.push(state.tableGame.endsAt);
  // A sleeping phone already replaced stays called, its time long past: only what is still to come.
  const ahead = times.filter((t) => t > now);
  return ahead.length > 0 ? Math.min(...ahead) : null;
}

/**
 * The points the stop's end gives, from its meeting and its alarms (`STOP_POINTS`): seat → points, and
 * seat → why. `saboteurs` are the stop's; `meeting` its own meeting, closed, or null when it never met.
 */
export function stopScore(
  state: Pick<ShowState, 'votes' | 'game'>,
  chapter: Chapter,
  saboteurs: readonly number[],
  meeting: Meeting | null,
): { points: Record<string, number>; why: Record<string, StopReason[]> } {
  const points: Record<string, number> = {};
  const why: Record<string, StopReason[]> = {};
  const add = (seat: number, reason: StopReason) => {
    points[String(seat)] = (points[String(seat)] ?? 0) + STOP_POINTS[reason];
    (why[String(seat)] ??= []).push(reason);
  };
  const bad = new Set(saboteurs);
  if (meeting) {
    for (const [voter, ballot] of Object.entries(state.votes[meeting.id] ?? {})) {
      if (/^\d+$/.test(ballot) && bad.has(Number(ballot))) add(Number(voter), 'RIGHT_VOTE');
    }
  }
  const out = meeting?.result?.sentToBrig ?? null;
  if (out !== null) add(out, bad.has(out) ? 'CAUGHT' : 'WRONGLY_OUT');
  for (const seat of saboteurs) if (seat !== out) add(seat, 'ESCAPED');
  for (const a of state.game.anomalies) {
    if (a.chapter === chapter && a.fracturedAt !== null && typeof a.by === 'number' && bad.has(a.by)) add(a.by, 'DAMAGE');
  }
  return { points, why };
}

/** Just the points of `stopScore`. */
export function stopPoints(...args: Parameters<typeof stopScore>): Record<string, number> {
  return stopScore(...args).points;
}
